import { useCallback, useEffect, useState } from "react";
import { motion } from "motion/react";
import type { EmailKind, EmailPrefs } from "../../../shared/types.ts";
import { Button, cn, spring } from "../../ui/index.ts";
import { useField } from "../../ui/Field.tsx";
import { errorText, getEmailPrefs, saveEmailPrefs } from "../../lib/api.ts";
import { useApp } from "../../lib/ctx.ts";
import { Loading } from "../parts.tsx";
import { Row } from "./parts.tsx";

const ROWS: { kind: EmailKind; label: string; hint: string }[] = [
  { kind: "setup_reminder", label: "Setup reminders", hint: "A reminder to finish setting up your account." },
  { kind: "application_updates", label: "Application updates", hint: "When an application needs your answer to continue." },
  { kind: "limit_reached", label: "Free plan limit", hint: "When you've used all your free applications for the month." },
  { kind: "new_matches", label: "Job match updates", hint: "New jobs that match your preferences while you're away." },
];

/** The Emails settings section: a switch per email kind, each saved as soon as it flips. */
export function EmailRows() {
  const app = useApp();
  const [prefs, setPrefs] = useState<EmailPrefs | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    getEmailPrefs().then(setPrefs, () => setFailed(true));
  }, []);

  useEffect(load, [load]);

  if (failed)
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-6 md:py-5">
        <p className="text-sm text-ink-soft">Your email preferences didn't load.</p>
        <Button variant="secondary" onClick={load}>
          Try again
        </Button>
      </div>
    );
  if (!prefs)
    return (
      <div className="px-5 py-4 md:px-6 md:py-5">
        <Loading row label="Loading your email preferences" />
      </div>
    );

  const flip = (kind: EmailKind, on: boolean) => {
    setPrefs((p) => p && { ...p, [kind]: on });
    saveEmailPrefs({ [kind]: on }).catch((e) => {
      setPrefs((p) => p && { ...p, [kind]: !on });
      app.toast(errorText(e), "bad");
    });
  };

  return ROWS.map((r) => (
    <Row key={r.kind} label={r.label} hint={r.hint}>
      <Toggle on={prefs[r.kind]} onChange={(on) => flip(r.kind, on)} />
    </Row>
  ));
}

/** An on/off switch named by the Row it sits in. */
function Toggle({ on, onChange }: { on: boolean; onChange: (on: boolean) => void }) {
  const field = useField();
  return (
    <button
      type="button"
      role="switch"
      id={field?.id}
      aria-checked={on}
      onClick={() => onChange(!on)}
      className="relative flex h-10 items-center rounded-control before:absolute before:-inset-x-2 before:inset-y-0 before:content-['']"
    >
      <span aria-hidden className={cn("relative h-5 w-9 shrink-0 rounded-chip transition-[background-color] duration-150", on ? "bg-primary" : "bg-ink/15")}>
        <motion.span className="absolute top-0.5 size-4 rounded-sm bg-white shadow-soft" initial={false} animate={{ left: on ? 18 : 2 }} transition={spring} />
      </span>
    </button>
  );
}
